"use client";

import { Badge, Button, ScrollArea, cn } from "@wtc/ui";
import { Clock, Sparkles, Star, Tag } from "lucide-react";

import type { EmailTemplateLike } from "../types";

export interface TemplatePreviewPaneProps<T extends EmailTemplateLike> {
  selectedTemplate: T | null;
  isFavorite: boolean;
  onToggleFavorite: (id: string) => void;
  onApplyTemplate: (template: T) => void;
  /**
   * Unfilled `{{variables}}` for the selected template, when the host resolved a
   * preview against the recipient. Surfaced so a rep sees what they'll be asked
   * to fill BEFORE inserting (the strict resolver blocks the send otherwise).
   */
  unfilledVariables?: string[];
}

export function TemplatePreviewPane<T extends EmailTemplateLike>({
  selectedTemplate,
  isFavorite,
  onToggleFavorite,
  onApplyTemplate,
  unfilledVariables,
}: TemplatePreviewPaneProps<T>) {
  return (
    <div className="border rounded-md flex flex-col min-h-0">
      {!selectedTemplate ? (
        <div className="flex-1 flex items-center justify-center p-6 text-center text-muted-foreground">
          <div className="space-y-2">
            <Sparkles className="h-6 w-6 mx-auto" />
            <p className="text-sm">Select a template to preview and insert.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="p-4 border-b space-y-2">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-sm text-card-foreground">
                {selectedTemplate.name}
              </h3>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  type="button"
                  variant={isFavorite ? "secondary" : "outline"}
                  size="sm"
                  className="shrink-0 gap-1.5"
                  aria-pressed={isFavorite}
                  title={isFavorite ? "Remove from favorites" : "Add to favorites"}
                  onClick={() => onToggleFavorite(selectedTemplate.id)}
                >
                  <Star
                    className={cn(
                      "h-4 w-4 shrink-0",
                      isFavorite && "fill-amber-400 text-amber-500"
                    )}
                  />
                  <span className="max-w-[140px] truncate">
                    {isFavorite ? "Remove favorite" : "Add favorite"}
                  </span>
                </Button>
                <Button type="button" size="sm" onClick={() => onApplyTemplate(selectedTemplate)}>
                  Use Template
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground break-words">
              Subject: {selectedTemplate.subject || "—"}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {selectedTemplate.is_shared && (
                <Badge variant="outline" className="text-[10px] h-5">
                  Shared
                </Badge>
              )}
              {selectedTemplate.tags?.map((tag) => (
                <Badge key={tag} variant="secondary" className="text-[10px] h-5">
                  <Tag className="h-2.5 w-2.5 mr-1" />
                  {tag}
                </Badge>
              ))}
              {selectedTemplate.created_at && (
                <Badge variant="outline" className="text-[10px] h-5">
                  <Clock className="h-2.5 w-2.5 mr-1" />
                  {new Date(selectedTemplate.created_at).toLocaleDateString()}
                </Badge>
              )}
            </div>
            {unfilledVariables && unfilledVariables.length > 0 && (
              <p className="text-[11px] text-amber-700">
                Needs a value before sending: {unfilledVariables.map((v) => `{{${v}}}`).join(", ")}
              </p>
            )}
          </div>
          <div className="flex-1 min-h-0 p-4">
            <ScrollArea className="h-full">
              <div className="border rounded-md overflow-hidden bg-white">
                <iframe
                  title={`Preview ${selectedTemplate.name}`}
                  srcDoc={selectedTemplate.body || "<p>No template body available.</p>"}
                  className="w-full min-h-[420px]"
                  sandbox=""
                />
              </div>
            </ScrollArea>
          </div>
        </>
      )}
    </div>
  );
}
